import { Grid } from "@mui/material";
import { FormSection } from "Components/Shared/FormSection";
import { DateFormInput } from "Components/Shared/Inputs/Form/DateFormInput";
import { Dropdown } from "Components/Shared/Inputs/Form/Dropdown";
import { CountryDropdown } from "Components/Shared/Inputs/Form/Dropdowns/CountryDropdown";
import { IdentificationCardDropdown } from "Components/Shared/Inputs/Form/Dropdowns/IdentificationCardDropdown";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PhoneInput } from "Components/Shared/Inputs/Form/PhoneInput";
import { AddressFields } from "Components/Users/UserDetailPageContent/Components/AddressFields";
import type {
  RelatedPartyFormDataInput,
  RelatedPartyFormData,
} from "Components/Users/schemas";
import { useGenderList } from "Hooks/Options/useGenderList";
import { useCheckPermission } from "Hooks/usePermission";
import { type AddressDto, Permission } from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { Resources, useResource } from "Translations/Resources";
import type { ReactNode } from "react";
import type { Control } from "react-hook-form";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.RelatedParties.Form.Fields
    .RelatedPartyFields;

const IDENTIFICATION_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form
    .PartyIdentification;

const ONBOARDING_RESOURCES = Resources.Onboarding.Steps.Documents;

type Props = {
  control: Control<RelatedPartyFormDataInput, unknown, RelatedPartyFormData>;
  addresses: Array<AddressDto>;
  partyPublicID?: string;
  firstDocumentImagesSlot?: ReactNode;
  secondDocumentImagesSlot?: ReactNode;
  areIdentityFieldsReadOnly?: boolean;
};

export const RelatedPartyFormFields = ({
  control,
  addresses,
  partyPublicID,
  firstDocumentImagesSlot,
  secondDocumentImagesSlot,
  areIdentityFieldsReadOnly,
}: Props) => {
  const { t } = useResource();
  const genderList = useGenderList();
  const canSeeDocuments = useCheckPermission(
    AccessRightCodes.IDDocuments,
    Permission.Read,
  );

  return (
    <>
      <FormSection legend={t(COMPONENT_RESOURCES.Legend.PersonalData)}>
        <FormInput
          control={control}
          name="firstName"
          label={t(COMPONENT_RESOURCES.FirstName)}
        />
        <FormInput
          control={control}
          name="lastName"
          label={t(COMPONENT_RESOURCES.LastName)}
        />
        <FormInput
          control={control}
          name="personalNumber"
          label={t(COMPONENT_RESOURCES.PersonalNumber)}
          mask="000000/0000"
          isReadOnly={areIdentityFieldsReadOnly}
          disabled={areIdentityFieldsReadOnly}
        />
        <DateFormInput
          control={control}
          name="birthDate"
          label={t(COMPONENT_RESOURCES.BirthDate)}
          disabled={areIdentityFieldsReadOnly}
        />
        <Dropdown
          control={control}
          name="gender"
          label={t(COMPONENT_RESOURCES.Gender)}
          codeList={genderList}
          disabled={areIdentityFieldsReadOnly}
        />
        <FormInput
          control={control}
          name="birthPlace"
          label={t(COMPONENT_RESOURCES.BirthPlace)}
        />
        <CountryDropdown
          control={control}
          name="nationality"
          label={t(COMPONENT_RESOURCES.Nationality)}
        />
      </FormSection>
      <FormSection legend={t(COMPONENT_RESOURCES.Legend.Contact)}>
        <PhoneInput
          control={control}
          name="phone"
          label={t(COMPONENT_RESOURCES.Phone)}
        />
        <FormInput
          control={control}
          name="email"
          label={t(COMPONENT_RESOURCES.Email)}
          type="email"
          valueTransformer={(v) => v.toLowerCase()}
        />
      </FormSection>
      {canSeeDocuments && (
        <>
          <FormSection legend={t(COMPONENT_RESOURCES.Legend.FirstDocument)}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <IdentificationCardDropdown
                  control={control}
                  name="partyIdentification.identification"
                  label={t(IDENTIFICATION_RESOURCES.Identification)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormInput
                  control={control}
                  name="partyIdentification.identificationNumber"
                  label={t(IDENTIFICATION_RESOURCES.IdentificationNumber)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateFormInput
                  control={control}
                  name="partyIdentification.identificationValidFrom"
                  label={t(IDENTIFICATION_RESOURCES.IdentificationValidFrom)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateFormInput
                  control={control}
                  name="partyIdentification.identificationValidTo"
                  label={t(IDENTIFICATION_RESOURCES.IdentificationValidTo)}
                />
              </Grid>
              <Grid size={12}>
                <FormInput
                  control={control}
                  name="partyIdentification.identificationIssuer"
                  label={t(IDENTIFICATION_RESOURCES.IdentificationIssuer)}
                />
              </Grid>
              {firstDocumentImagesSlot && (
                <Grid size={12}>{firstDocumentImagesSlot}</Grid>
              )}
            </Grid>
          </FormSection>
          <FormSection legend={t(ONBOARDING_RESOURCES.SecondDocumentTitle)}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <IdentificationCardDropdown
                  control={control}
                  name="partyIdentification.secondIdentification"
                  label={t(IDENTIFICATION_RESOURCES.Identification)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormInput
                  control={control}
                  name="partyIdentification.secondIdentificationNumber"
                  label={t(IDENTIFICATION_RESOURCES.IdentificationNumber)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateFormInput
                  control={control}
                  name="partyIdentification.secondIdentificationValidFrom"
                  label={t(IDENTIFICATION_RESOURCES.IdentificationValidFrom)}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateFormInput
                  control={control}
                  name="partyIdentification.secondIdentificationValidTo"
                  label={t(IDENTIFICATION_RESOURCES.IdentificationValidTo)}
                />
              </Grid>
              <Grid size={12}>
                <FormInput
                  control={control}
                  name="partyIdentification.secondIdentificationIssuer"
                  label={t(IDENTIFICATION_RESOURCES.IdentificationIssuer)}
                />
              </Grid>
              {secondDocumentImagesSlot && (
                <Grid size={12}>{secondDocumentImagesSlot}</Grid>
              )}
            </Grid>
          </FormSection>
        </>
      )}
      <AddressFields
        control={control}
        addresses={addresses}
        partyPublicID={partyPublicID}
      />
    </>
  );
};
